import { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react'
import { Play3, Pause, VolumeOff, VolumeOn, NoSound } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Button } from 'blaze-ui/Button/Button'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { MediaObject } from 'types'
import { Spinner } from 'common/Spinner/Spinner'
import Styles from './VideoPlayer.module.scss'
import cx from 'classnames'
import { useRemoveDesignAudio } from 'hooks/queries/designs/useRemoveDesignAudio'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useModals } from 'almanac-ui/Modal'
import { AudioEditorModal } from './AudioEditorModal/AudioEditorModal'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { removeFileDetailCache } from 'hooks/queries/files/cacheUtils'

interface VideoPlayerProps {
  mediaObject: MediaObject
  className?: string
  isActive?: boolean
  videoRef?: React.RefObject<HTMLVideoElement>
  almanacFileKey: string
  isGenerating?: boolean
}

type VideoElement = HTMLVideoElement & {
  mozHasAudio?: boolean
  webkitAudioDecodedByteCount?: number
  audioTracks?: { length: number }
}

type AudioState = 'muted' | 'unmuted' | 'noSound' | undefined

export const VideoPlayer = memo(
  ({
    mediaObject,
    className,
    isActive = true,
    videoRef: externalVideoRef,
    almanacFileKey,
    isGenerating = false,
  }: VideoPlayerProps) => {
    const queryClient = useQueryClient()
    const workspaceId = useCurrentWorkspaceId()
    const { openModal } = useModals()
    const internalVideoRef = useRef<HTMLVideoElement>(null)
    const videoRef = externalVideoRef || internalVideoRef
    const dismissToastRef = useRef<(() => void) | null>(null)
    const [isPlaying, setIsPlaying] = useState(true)
    const [isLoading, setIsLoading] = useState(true)
    const [isHovered, setIsHovered] = useState(false)
    const [audioState, setAudioState] = useState<AudioState>(undefined)
    const audioEditorModalEnabled = useFlipperFlag('audio_editor_modal')
    const { isMobile } = useResponsiveDesign()

    // Ensures the conatiner reserves space for the video when it is loading
    const loadingContainerStyle = useMemo<React.CSSProperties | undefined>(() => {
      if (!isLoading) return undefined

      const ratioFromDimensions =
        mediaObject.width && mediaObject.height && mediaObject.width > 0 && mediaObject.height > 0
          ? mediaObject.width / mediaObject.height
          : undefined
      const ratio =
        ratioFromDimensions ??
        (mediaObject.aspect_ratio && mediaObject.aspect_ratio > 0 ? mediaObject.aspect_ratio : undefined)

      return ratio && isFinite(ratio) ? { aspectRatio: ratio } : undefined
    }, [isLoading, mediaObject.aspect_ratio, mediaObject.height, mediaObject.width])

    const handleAudioTransformCompleted = useCallback(() => {
      removeFileDetailCache(queryClient, workspaceId, almanacFileKey)
      queryClient.invalidateQueries({ queryKey: calendarKeys.detail(almanacFileKey) })
    }, [queryClient, workspaceId, almanacFileKey])

    const soundButtonConfig = {
      muted: {
        label: 'Unmute video',
        icon: VolumeOff,
        tooltip: 'Sound is off, click to unmute',
      },
      unmuted: {
        label: 'Mute video',
        icon: VolumeOn,
        tooltip: 'Sound is on, click to mute',
      },
      noSound: {
        label: 'No sound',
        icon: NoSound,
        tooltip: 'This video has no sound',
      },
    }

    const handlePlayPause = () => {
      const video = videoRef.current
      if (!video || isLoading) return

      setVideoPlayState(!isPlaying)
    }

    const setVideoPlayState = useCallback(
      (shouldPlay: boolean) => {
        const video = videoRef.current
        if (!video || isLoading) return

        if (shouldPlay) {
          video.play()
        } else {
          video.pause()
        }
      },
      [isLoading, videoRef],
    )

    useEffect(() => {
      const video = videoRef.current
      if (!video || isLoading) return

      setVideoPlayState(isActive)
    }, [isActive, isLoading, setVideoPlayState, videoRef])

    const checkForAudio = useCallback((video: VideoElement) => {
      const hasAudio =
        video.mozHasAudio ||
        Boolean(video.webkitAudioDecodedByteCount) ||
        Boolean(video.audioTracks && video.audioTracks.length)

      setAudioState(hasAudio ? 'muted' : 'noSound')
    }, [])

    const removeDesignAudioMutation = useRemoveDesignAudio(
      { almanacFileKey },
      {
        onSuccess: () => {
          dismissToastRef.current?.()
          showToastMessage('Audio removed successfully', 'success')
          setAudioState('noSound')

          queryClient.invalidateQueries({ queryKey: calendarKeys.detail(almanacFileKey) })
        },
      },
    )

    const handleRemoveAudio = () => {
      dismissToastRef.current = showToastMessage('Removing audio...', 'loading', undefined, 0)
      removeDesignAudioMutation.mutate()
    }

    const handleOpenAudioModal = () => {
      if (isPlaying) {
        handlePlayPause()
      }

      openModal(AudioEditorModal, {
        mediaObject,
        almanacFileKey,
        onAudioTransformComplete: handleAudioTransformCompleted,
      })
    }

    if (isGenerating) {
      return (
        <div className={cx(Styles.videoContainer, Styles.loading, className)}>
          <div className={Styles.generatingContent}>
            <Spinner size="large" className={Styles.loadingSpinner} />
          </div>
        </div>
      )
    }

    return (
      <div
        className={cx(Styles.videoContainer, { [Styles.loading]: isLoading }, className)}
        style={loadingContainerStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <video
          ref={videoRef}
          src={mediaObject.source_url}
          onClick={(e) => {
            e.stopPropagation()
            handlePlayPause()
          }}
          onCanPlay={() => setIsLoading(false)}
          autoPlay={isActive}
          muted={audioState !== 'unmuted'}
          loop
          onPlay={() => setIsPlaying(true)}
          onTimeUpdate={(e) => {
            if (audioState !== undefined) return

            const video = e.currentTarget as VideoElement
            checkForAudio(video)
          }}
          onPause={() => setIsPlaying(false)}
          playsInline
          preload="metadata"
          data-testid="video-player"
        />

        {isLoading && <Spinner className={Styles.loadingSpinner} size="large" />}
        {!isLoading && (
          <>
            {isHovered && (
              <>
                <div className={Styles.playPauseOverlay}>{isPlaying ? <Pause size={20} /> : <Play3 size={20} />}</div>
                <div className={Styles.audioButtonContainer}>
                  {audioState &&
                    audioState !== 'noSound' &&
                    mediaObject.audio &&
                    !audioEditorModalEnabled &&
                    !isMobile && (
                      <Button
                        variant="secondary"
                        onPress={handleRemoveAudio}
                        isDisabled={removeDesignAudioMutation.isLoading}
                        frontIcon={NoSound}
                        className={Styles.audioButton}
                      >
                        Remove Audio
                      </Button>
                    )}
                  {audioEditorModalEnabled && !isMobile && (
                    <Button variant="secondary" onPress={handleOpenAudioModal} className={Styles.audioButton}>
                      {mediaObject.audio ? 'Edit Audio' : 'Add Audio'}
                    </Button>
                  )}
                </div>
              </>
            )}
            {audioState === 'noSound' && (
              <Tooltip label={soundButtonConfig[audioState].tooltip}>
                <div className={Styles.noSoundContainer} aria-label={soundButtonConfig[audioState].label}>
                  <NoSound size={20} />
                </div>
              </Tooltip>
            )}
            {audioState && audioState !== 'noSound' && (
              <Tooltip label={soundButtonConfig[audioState].tooltip}>
                <IconButton
                  className={Styles.muteButton}
                  onPress={() => setAudioState(audioState === 'muted' ? 'unmuted' : 'muted')}
                  aria-label={soundButtonConfig[audioState].label}
                  size="xs"
                  rounded
                  icon={soundButtonConfig[audioState].icon}
                />
              </Tooltip>
            )}
          </>
        )}
      </div>
    )
  },
)
